export const WEB_ANALYTICS_TRANSPORT_DEADLINE_MS = 15_000;

class AnalyticsTransportTimeoutError extends Error {
  constructor() {
    super("Browser analytics transport exceeded its deadline");
    this.name = "TimeoutError";
  }
}

/**
 * Bounds and aborts one transport attempt. Event retries are server-deduplicated; observation
 * retries retain their original timestamp so backend ordering makes a post-commit retry harmless.
 */
export async function withAnalyticsTransportDeadline<Result>(
  operation: (signal: AbortSignal) => Promise<Result>,
): Promise<Result> {
  const controller = new AbortController();
  let timeout: ReturnType<typeof globalThis.setTimeout> | undefined;
  const deadline = new Promise<never>((_, reject) => {
    timeout = globalThis.setTimeout(() => {
      controller.abort();
      reject(new AnalyticsTransportTimeoutError());
    }, WEB_ANALYTICS_TRANSPORT_DEADLINE_MS);
  });

  try {
    // Start the transport in the caller's task so navigation-triggered keepalive drains are
    // registered with the browser before the document begins unloading.
    return await Promise.race([operation(controller.signal), deadline]);
  } finally {
    if (timeout !== undefined) globalThis.clearTimeout(timeout);
  }
}
